import {
  QuestionnaireFormProps,
  QuestionnaireQuestions,
} from '../../../models';
import { useEffect, useState } from 'react';

import PhoneInput, { PhoneInputProps } from 'react-phone-input-2';
import { Header } from '../../Screen';

import './style.scss';

const GERMAN_PHONE_NO_MASK = '... ... ..........';

export type PhoneFormProps<Questionnaire extends QuestionnaireQuestions> =
  {
    countryCode?: string;
    validate?: (value: string) => boolean;
    minLength?: number;
    maxLength?: number;
  } & QuestionnaireFormProps<Questionnaire, string>
    & PhoneInputProps;

export const PhoneForm = <Questionnaire extends QuestionnaireQuestions>({
  value,
  question,
  onSubmitValue,
  featureName,
  placeholder = "+49 111 111 1111",
  validate,
  setValidForSubmission,
  countryCode,
  minLength,
  maxLength,
}: PhoneFormProps<Questionnaire>) => {
  const [phoneNumber, setPhoneNumber] = useState<string>(value ?? '');

  useEffect(() => {
    const phoneNumberLength = phoneNumber?.length;
    const isValid = phoneNumberLength > 0 && (validate ? validate(phoneNumber) : true);
    const isValidMaxLength = !maxLength ? true : phoneNumberLength <= maxLength;
    const isValidMinLength = !minLength ? true : phoneNumberLength >= minLength;

    setValidForSubmission(isValid && isValidMaxLength && isValidMinLength);
  }, [maxLength, minLength, phoneNumber, setValidForSubmission, validate]);

  const onChange = (updatedValue: string) => {
    setPhoneNumber(updatedValue);
  };

  const handleSubmit = () => {
    if (!phoneNumber) {
      throw new Error(
        `[${featureName}] Input phone number value is missing for submission for question: ${question}`
      );
    }

    onSubmitValue(phoneNumber);
  };

  return (
    <Header onSubmit={handleSubmit}>
      <PhoneInput
        value={phoneNumber}
        onChange={onChange}
        defaultMask={{
          es:'... ... ...',
          default: GERMAN_PHONE_NO_MASK
        }?.[countryCode|| 'default']}
        alwaysDefaultMask
        placeholder={placeholder}
        enableLongNumbers={!countryCode}
        {...countryCode ? {
          country: countryCode,
          countryCodeEditable: false,
          disableDropdown: true,
          disableCountryCode: false,
          onlyCountries: [countryCode],
        } : {}}
        isValid={validate ? validate(phoneNumber) : true}
      />
    </Header>
  );
};
